import React, { useState, useEffect, useCallback } from "react";
import { Button, Form } from "react-bootstrap";
import myAxios from "../../_services/axiositeachWrapper";
import Config from "../../_config/config";
import GradeCardService from "../../_services/GradeCardService";
import PerStudentForm from "./components/PerStudentForm";
import BulkEntryGrid from "./components/BulkEntryGrid";

interface StudentRow {
  EnquiryId: number;
  EnrollmentId: number;
  StudentName: string;
  StudentCode: string | null;
  GradeCardId: number | null;
  GradeCardStatus: string | null;
}

/**
 * Grade Card Entry Page — Teacher enters assessment data.
 * Two modes: Per-Student (full form) and Per-Item (bulk grid).
 */
const GradeCardEntryPage: React.FC = () => {
  // Batch selection
  const [batches, setBatches] = useState<any[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [loadingBatches, setLoadingBatches] = useState(true);

  // Structure resolution
  const [structures, setStructures] = useState<any[]>([]);
  const [selectedStructureId, setSelectedStructureId] = useState<number | null>(null);
  const [structure, setStructure] = useState<any>(null);
  const [loadingStructure, setLoadingStructure] = useState(false);

  // Term selection
  const [termIndex, setTermIndex] = useState(0);

  // Students
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Mode
  const [mode, setMode] = useState<"per-student" | "bulk">("per-student");
  const [selectedStudent, setSelectedStudent] = useState<StudentRow | null>(null);

  // Load batches on mount
  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    setLoadingBatches(true);
    try {
      const res: any = await myAxios.get(
        `${Config.nodeSiteUrl}v1/grade-cards/batches`,
      );
      setBatches(res.data?.data || []);
    } catch (e) {
      console.error("Failed to load batches", e);
    } finally {
      setLoadingBatches(false);
    }
  };

  // When batch changes, load structures
  useEffect(() => {
    if (!selectedBatchId) {
      setStructures([]);
      setSelectedStructureId(null);
      return;
    }
    loadStructures();
  }, [selectedBatchId]);

  const loadStructures = async () => {
    try {
      const res = await GradeCardService.listStructures();
      const data = res.data?.data || [];
      setStructures(data);
      if (data.length === 1) {
        setSelectedStructureId(data[0].Id);
      }
    } catch (e) {
      console.error("Failed to load structures", e);
    }
  };

  // When structure changes, load full detail
  useEffect(() => {
    if (!selectedStructureId) {
      setStructure(null);
      return;
    }
    loadStructureDetail();
  }, [selectedStructureId]);

  const loadStructureDetail = async () => {
    setLoadingStructure(true);
    try {
      const res = await GradeCardService.getStructure(selectedStructureId!);
      setStructure(res.data?.data || null);
      setTermIndex(0);
    } catch (e) {
      console.error("Failed to load structure", e);
    } finally {
      setLoadingStructure(false);
    }
  };

  // When batch + structure + term change, load students
  useEffect(() => {
    if (!selectedBatchId || !selectedStructureId) {
      setStudents([]);
      return;
    }
    loadStudents();
  }, [selectedBatchId, selectedStructureId, termIndex]);

  const loadStudents = async () => {
    setLoadingStudents(true);
    try {
      const res = await GradeCardService.getStudentsForEntry({
        batchId: selectedBatchId!,
        structureId: selectedStructureId!,
        termIndex,
      });
      setStudents(res.data?.data || []);
    } catch (e) {
      console.error("Failed to load students", e);
    } finally {
      setLoadingStudents(false);
    }
  };

  const termLabels: string[] = structure
    ? typeof structure.TermLabels === "string"
      ? JSON.parse(structure.TermLabels)
      : structure.TermLabels || []
    : [];

  // If a student is selected in per-student mode, show the form
  if (selectedStudent && structure) {
    return (
      <div style={{ padding: 20 }}>
        <PerStudentForm
          enquiryId={selectedStudent.EnquiryId}
          enrollmentId={selectedStudent.EnrollmentId}
          studentName={selectedStudent.StudentName}
          structureId={selectedStructureId!}
          termIndex={termIndex}
          structure={structure}
          onBack={() => setSelectedStudent(null)}
          onSaved={() => {
            setSelectedStudent(null);
            loadStudents();
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <h4 style={{ marginBottom: 20 }}>Grade Card Entry</h4>

      {/* Filters bar */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "flex-end",
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <Form.Group>
          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
            Batch
          </Form.Label>
          <Form.Select
            size="sm"
            style={{ width: 220 }}
            value={selectedBatchId || ""}
            onChange={(e) =>
              setSelectedBatchId(e.target.value ? Number(e.target.value) : null)
            }
            disabled={loadingBatches}
          >
            <option value="">
              {loadingBatches ? "Loading..." : "Select Batch"}
            </option>
            {batches.map((b: any) => (
              <option key={b.Id} value={b.Id}>
                {b.Name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {structures.length > 1 && (
          <Form.Group>
            <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
              Grade Card Template
            </Form.Label>
            <Form.Select
              size="sm"
              style={{ width: 220 }}
              value={selectedStructureId || ""}
              onChange={(e) =>
                setSelectedStructureId(
                  e.target.value ? Number(e.target.value) : null,
                )
              }
            >
              <option value="">Select Template</option>
              {structures.map((s: any) => (
                <option key={s.Id} value={s.Id}>
                  {s.Name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {structure && termLabels.length > 0 && (
          <Form.Group>
            <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
              Term
            </Form.Label>
            <Form.Select
              size="sm"
              style={{ width: 140 }}
              value={termIndex}
              onChange={(e) => setTermIndex(Number(e.target.value))}
            >
              {termLabels.map((label: string, i: number) => (
                <option key={i} value={i}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {selectedBatchId && selectedStructureId && (
          <div style={{ marginLeft: "auto" }}>
            <div
              className="btn-group btn-group-sm"
              role="group"
            >
              <button
                type="button"
                className={`btn ${mode === "per-student" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setMode("per-student")}
              >
                Per Student
              </button>
              <button
                type="button"
                className={`btn ${mode === "bulk" ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setMode("bulk")}
              >
                Bulk Entry
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {!selectedBatchId ? (
        <div className="text-center text-muted" style={{ paddingTop: 40 }}>
          Select a batch to begin entering grade card data.
        </div>
      ) : loadingStructure ? (
        <div className="text-center py-4">Loading template...</div>
      ) : !structure ? (
        <div className="text-center text-muted" style={{ paddingTop: 40 }}>
          {structures.length === 0
            ? "No grade card templates found. Create one in Grade Card Templates."
            : "Select a template to continue."}
        </div>
      ) : mode === "bulk" ? (
        <BulkEntryGrid
          students={students}
          structure={structure}
          structureId={selectedStructureId!}
          termIndex={termIndex}
          onSaved={loadStudents}
        />
      ) : (
        // Per-student mode: show student list
        <>
          {loadingStudents ? (
            <div className="text-center py-4">Loading students...</div>
          ) : students.length === 0 ? (
            <div className="text-center text-muted py-4">
              No students found in this batch.
            </div>
          ) : (
            <table className="table table-bordered table-hover">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Student Code</th>
                  <th>Student Name</th>
                  <th style={{ width: 120 }}>Status</th>
                  <th style={{ width: 100 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.EnquiryId}>
                    <td style={{ fontFamily: "monospace", fontSize: 13 }}>{s.StudentCode || "—"}</td>
                    <td>{s.StudentName}</td>
                    <td>
                      {s.GradeCardStatus === "published" ? (
                        <span
                          style={{
                            padding: "2px 8px",
                            fontSize: 11,
                            borderRadius: 4,
                            background: "var(--status-success-bg)",
                            color: "var(--status-success-text)",
                          }}
                        >
                          Published
                        </span>
                      ) : s.GradeCardStatus === "draft" ? (
                        <span
                          style={{
                            padding: "2px 8px",
                            fontSize: 11,
                            borderRadius: 4,
                            background: "var(--status-warning-bg)",
                            color: "var(--status-warning-text)",
                          }}
                        >
                          Draft
                        </span>
                      ) : (
                        <span
                          style={{
                            padding: "2px 8px",
                            fontSize: 11,
                            borderRadius: 4,
                            background: "var(--surface-sunken)",
                            color: "var(--text-faint)",
                          }}
                        >
                          Empty
                        </span>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setSelectedStudent(s)}
                      >
                        Fill
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default GradeCardEntryPage;
